import { Box, CloseButton, Group, ScrollArea, Space, Stack, Text } from '@mantine/core'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { BOGGLE_BADGE_DEFINITIONS } from '~/boggle/config/badges/registry'
import type {
  BoggleBadgeDefinition,
  BoggleBadgeId,
  BoggleBadgeLevel,
} from '~/boggle/config/badges/types'
import {
  BOGGLE_RUN_SUMMARY_RATINGS,
  type BoggleRunSummaryRatingConfig,
  type BoggleRunSummaryRatingId,
} from '~/boggle/config/runSummaryScore'
import type {
  BoggleBadgeProgressProjection,
  BoggleUnviewedBadge,
} from '~/boggle/progress/types'
import { isBoggleUnviewedBadgeVisibleForFilter } from '~/boggle/progress/unviewedProgress'
import { RatingFilter, type RatingFilterValue } from '~/boggle/ui/ratings/RatingFilter'
import {
  UnviewedImprovedBadgeIndicator,
  UnviewedNewBadgeIndicator,
} from '~/boggle/ui/unviewed/UnviewedItemIndicators'

import { BadgeMedallion } from './BadgeMedallion'
import { BadgePopover } from './BadgePopover'
import { filterCompendiumBadges } from './compendiumBadgeVisibility'

type CompendiumFilterId = RatingFilterValue

const EMPTY_COUNTS: Partial<Record<BoggleRunSummaryRatingId, number>> = {}

type CompendiumBadgeTileSnapshot = {
  countsByRating: Partial<Record<BoggleRunSummaryRatingId, number>>
  highestRating: BoggleRunSummaryRatingId | null
  unviewed: BoggleUnviewedBadge | null
}

/** Ratings shown in the filter row (left → right authored order, `none` excluded). */
const FILTER_RATINGS: readonly BoggleRunSummaryRatingConfig[] =
  BOGGLE_RUN_SUMMARY_RATINGS.filter(rating => rating.id !== 'none')

/**
 * Diamond + Ace filter chips stay hidden until the player has
 * completed at least one run with that rating. Lower tiers always appear so
 * the filter row stays predictable while the player makes progress.
 */
const GATED_FILTERS = new Set<BoggleRunSummaryRatingId>(['ace', 'diamond'])

/** Sections render in this order (no titles); each section is centered. */
const RARITY_SECTION_ORDER: readonly BoggleBadgeLevel[] = [
  'common',
  'uncommon',
  'rare',
  'legendary',
  'impossible',
]

export function compareCompendiumBadgeDefinitions(
  a: BoggleBadgeDefinition,
  b: BoggleBadgeDefinition,
): number {
  const tierDelta = (a.tierIndex ?? 1) - (b.tierIndex ?? 1)
  if (tierDelta !== 0) {
    return tierDelta
  }
  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
}

function isFilterUnlocked(
  ratingId: BoggleRunSummaryRatingId,
  profileRunsByRating: BoggleBadgeProgressProjection['profileRunsByRating'],
) {
  if (!GATED_FILTERS.has(ratingId)) {
    return true
  }
  return (profileRunsByRating[ratingId] ?? 0) > 0
}

export function buildCompendiumRatingFilterValues(
  profileRunsByRating: BoggleBadgeProgressProjection['profileRunsByRating'],
): readonly CompendiumFilterId[] {
  const list: CompendiumFilterId[] = ['all']
  for (const rating of FILTER_RATINGS) {
    if (isFilterUnlocked(rating.id, profileRunsByRating)) {
      list.push(rating.id)
    }
  }
  return list
}

function hasEarnedBadgeRecord(
  countsByRating: Partial<Record<BoggleRunSummaryRatingId, number>>,
  highestRating: BoggleRunSummaryRatingId | null,
) {
  if (highestRating !== null) {
    return true
  }
  return Object.values(countsByRating).some(n => (n ?? 0) > 0)
}

/**
 * Badge matches the rating filter when it was earned in a run at that rating,
 * or when its lifetime **highest** earned rating is at least as high as the
 * filter tier (e.g. a Diamond-tagged earn still matches a Gold filter).
 */
function badgeMatchesFilter(
  filter: CompendiumFilterId,
  countsByRating: Partial<Record<BoggleRunSummaryRatingId, number>>,
  highestRating: BoggleRunSummaryRatingId | null,
) {
  if (filter === 'all') {
    return true
  }
  if ((countsByRating[filter] ?? 0) > 0) {
    return true
  }
  if (highestRating === null) {
    return false
  }
  const filterIndex = BOGGLE_RUN_SUMMARY_RATINGS.findIndex(rating => rating.id === filter)
  const highestIndex = BOGGLE_RUN_SUMMARY_RATINGS.findIndex(
    rating => rating.id === highestRating,
  )
  if (filterIndex < 0 || highestIndex < 0) {
    return false
  }
  return highestIndex >= filterIndex
}

/**
 * Pure helper: given a filter and a per-badge progress record, return the
 * presentation-level lock used by the medallion + optional `displayHighest`
 * hint. Rating-corner visibility in the Compendium is handled separately (All
 * filter only). Filters never remove badges from the grid; instead,
 * non-matching badges render with the same locked treatment as
 * truly-unearned ones.
 */
export function resolveCompendiumBadgeDisplay({
  countsByRating,
  filter,
  highestRating,
}: {
  countsByRating: Partial<Record<BoggleRunSummaryRatingId, number>>
  filter: 'all' | BoggleRunSummaryRatingId
  highestRating: BoggleRunSummaryRatingId | null
}): {
  displayHighest: BoggleRunSummaryRatingId | null
  displayLocked: boolean
  matches: boolean
} {
  const matches = badgeMatchesFilter(filter, countsByRating, highestRating)
  const earned = hasEarnedBadgeRecord(countsByRating, highestRating)
  const displayLocked = !earned || !matches
  return {
    displayHighest: displayLocked ? null : highestRating,
    displayLocked,
    matches,
  }
}

export function countCompendiumUnlockedBadges(
  badges: readonly BoggleBadgeDefinition[],
  filter: CompendiumFilterId,
  perBadge: BoggleBadgeProgressProjection['perBadge'],
): number {
  let count = 0
  for (const definition of badges) {
    const record = perBadge[definition.id]
    const { displayLocked } = resolveCompendiumBadgeDisplay({
      countsByRating: record?.countsByRating ?? EMPTY_COUNTS,
      filter,
      highestRating: record?.highestRating ?? null,
    })
    if (!displayLocked) {
      count += 1
    }
  }
  return count
}

/**
 * Self-contained, screen-capable badge browser. The host decides whether to
 * render this inside a modal or a full screen. State is local: progress flows
 * in as a prop so tests + screens can supply their own snapshot without I/O.
 *
 * v1 product rules:
 *   - Four rarity sections render in Common / Uncommon / Rare / Legendary order, no titles.
 *   - Items within a section are centered, sorted by badge tier (I → IV) then name.
 *   - All badges always appear in every filter; non-matching badges render
 *     muted/locked. A badge matches a rating filter if it has a count for
 *     that rating or its highest earned rating is at least that tier.
 *   - The rating corner on medallions appears only when the filter is All.
 *   - Rating filter: shared Select control (same as Lexicon).
 *   - Diamond + Ace options appear only once the player has at least
 *     one completed run with that rating.
 *   - Locked badges show the full description for now (no mystery reveal yet).
 */
export function Compendium({
  badges = BOGGLE_BADGE_DEFINITIONS,
  onBadgeViewed,
  onClose,
  progress,
  revealHiddenLockedBadges = false,
  sessionUnviewedBadges = [],
}: {
  badges?: readonly BoggleBadgeDefinition[]
  onBadgeViewed?: (badgeId: BoggleUnviewedBadge['badgeId']) => void
  /** Optional close affordance; rendered as a top-right `CloseButton` when set. */
  onClose?: () => void
  progress: BoggleBadgeProgressProjection
  /** When true, hidden locked badges appear in the grid (dev mode). */
  revealHiddenLockedBadges?: boolean
  /** Snapshot taken when the compendium opens; cleared from storage at that moment. */
  sessionUnviewedBadges?: readonly BoggleUnviewedBadge[]
}) {
  const [filter, setFilter] = useState<CompendiumFilterId>('all')
  const [selectedBadgeId, setSelectedBadgeId] = useState<BoggleBadgeId | null>(null)

  const visibleBadges = useMemo(
    () => filterCompendiumBadges(badges, progress.perBadge, revealHiddenLockedBadges),
    [badges, progress.perBadge, revealHiddenLockedBadges],
  )

  const visibleFilters = useMemo(
    () => buildCompendiumRatingFilterValues(progress.profileRunsByRating),
    [progress.profileRunsByRating],
  )

  useEffect(() => {
    if (!visibleFilters.includes(filter)) {
      setFilter('all')
    }
  }, [filter, visibleFilters])

  const sections = useMemo(() => {
    const buckets: Record<BoggleBadgeLevel, BoggleBadgeDefinition[]> = {
      common: [],
      impossible: [],
      legendary: [],
      rare: [],
      uncommon: [],
    }
    for (const badge of visibleBadges) {
      buckets[badge.level].push(badge)
    }
    for (const level of RARITY_SECTION_ORDER) {
      buckets[level].sort(compareCompendiumBadgeDefinitions)
    }
    return RARITY_SECTION_ORDER.map(level => ({ badges: buckets[level], level }))
  }, [visibleBadges])

  const sessionUnviewedByBadgeId = useMemo(
    () => new Map(sessionUnviewedBadges.map(entry => [entry.badgeId, entry])),
    [sessionUnviewedBadges],
  )

  const badgeTileSnapshots = useMemo(() => {
    const snapshots = new Map<BoggleBadgeId, CompendiumBadgeTileSnapshot>()
    for (const definition of visibleBadges) {
      const record = progress.perBadge[definition.id]
      snapshots.set(definition.id, {
        countsByRating: record?.countsByRating ?? EMPTY_COUNTS,
        highestRating: record?.highestRating ?? null,
        unviewed: sessionUnviewedByBadgeId.get(definition.id) ?? null,
      })
    }
    return snapshots
  }, [visibleBadges, progress.perBadge, sessionUnviewedByBadgeId])

  const unlockedBadgeCount = useMemo(
    () => countCompendiumUnlockedBadges(visibleBadges, filter, progress.perBadge),
    [visibleBadges, filter, progress.perBadge],
  )

  const onBadgeViewedRef = useRef(onBadgeViewed)
  onBadgeViewedRef.current = onBadgeViewed

  const toggleBadgeSelection = useCallback(
    (badgeId: BoggleBadgeId) => {
      setSelectedBadgeId(prev => {
        const next = prev === badgeId ? null : badgeId
        if (next !== null && sessionUnviewedByBadgeId.has(badgeId)) {
          onBadgeViewedRef.current?.(badgeId)
        }
        return next
      })
    },
    [sessionUnviewedByBadgeId],
  )

  return (
    <Stack
      data-testid="boggle-compendium"
      gap={0}
      h="100%"
      mih={0}
      pos="relative"
      style={{ overflow: 'hidden' }}
    >
      {onClose ? (
        <CloseButton
          aria-label="Close compendium"
          data-testid="compendium-close"
          iconSize={28}
          onClick={onClose}
          pos="absolute"
          right={14}
          top={14}
          variant="transparent"
        />
      ) : null}
      <Group justify="space-between" pb={14} pr={14} pl={20} w="100%">
        <Group gap={6}>
          <Group gap={2} c="rgba(232, 224, 210, 0.7)" fz={15} align="center">
            <Text c="white" data-testid="compendium-unlocked-count" fz={18} lh={1}>
              {unlockedBadgeCount}
            </Text>
            <Text data-testid="compendium-unlocked-count" fz={15} lh={1}>
              /{visibleBadges.length}
            </Text>
          </Group>
          {/* <Text
            c="rgba(232, 224, 210, 0.7)"
            data-testid="compendium-unlocked-count"
            fz={13}
            lh={1}
          >
            discovered
          </Text> */}
        </Group>
        <Box pos="relative" w={140}>
          <RatingFilter
            ariaLabelPrefix="Filter badges by"
            onChange={setFilter}
            testIdPrefix="compendium-filter"
            value={filter}
            values={visibleFilters}
            variant="select"
          />
        </Box>
      </Group>
      <ScrollArea flex={1} mih={0} type="auto" w="100%" px={20}>
        <Stack align="center" gap={0} w="100%" pb={20}>
          {sections.map((section, sectionIndex) =>
            section.badges.length === 0 ? null : (
              <Box key={section.level} w="100%">
                {sectionIndex > 0 ? <Space h={24} w="100%" /> : null}
                <Group
                  data-testid={`compendium-section-${section.level}`}
                  gap={2}
                  justify="center"
                  wrap="wrap"
                >
                  {section.badges.map(definition => {
                    const snapshot = badgeTileSnapshots.get(definition.id)!
                    return (
                      <CompendiumBadgeTile
                        countsByRating={snapshot.countsByRating}
                        definition={definition}
                        filter={filter}
                        highestRating={snapshot.highestRating}
                        key={definition.id}
                        onToggleSelect={toggleBadgeSelection}
                        selected={selectedBadgeId === definition.id}
                        unviewed={snapshot.unviewed}
                      />
                    )
                  })}
                </Group>
              </Box>
            ),
          )}
        </Stack>
      </ScrollArea>
    </Stack>
  )
}

/** Compendium tile: memoized so only toggled badges re-render on selection. */
const CompendiumBadgeTile = memo(function CompendiumBadgeTile({
  countsByRating,
  definition,
  filter,
  highestRating,
  onToggleSelect,
  selected,
  unviewed,
}: {
  countsByRating: Partial<Record<BoggleRunSummaryRatingId, number>>
  definition: BoggleBadgeDefinition
  filter: CompendiumFilterId
  highestRating: BoggleRunSummaryRatingId | null
  onToggleSelect: (badgeId: BoggleBadgeId) => void
  selected: boolean
  unviewed: BoggleUnviewedBadge | null
}) {
  const { displayLocked } = resolveCompendiumBadgeDisplay({
    countsByRating,
    filter,
    highestRating,
  })
  const showUnviewed =
    unviewed !== null &&
    isBoggleUnviewedBadgeVisibleForFilter({
      countsByRating,
      filter,
      unviewed,
    })

  const handleToggle = useCallback(() => {
    onToggleSelect(definition.id)
  }, [definition.id, onToggleSelect])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && selected) {
        onToggleSelect(definition.id)
      }
    },
    [definition.id, onToggleSelect, selected],
  )

  let medallion = (
    <BadgeMedallion
      definition={definition}
      highestEarnedRating={filter === 'all' ? highestRating : null}
      locked={displayLocked}
      size="compact"
    />
  )

  if (showUnviewed && unviewed?.kind === 'new') {
    medallion = (
      <UnviewedNewBadgeIndicator active testId={`compendium-badge-new-${definition.id}`}>
        {medallion}
      </UnviewedNewBadgeIndicator>
    )
  } else if (showUnviewed && unviewed?.kind === 'improved') {
    medallion = (
      <UnviewedImprovedBadgeIndicator
        active
        testId={`compendium-badge-improved-${definition.id}`}
      >
        {medallion}
      </UnviewedImprovedBadgeIndicator>
    )
  }

  return (
    <BadgePopover
      countsByRating={countsByRating}
      definition={definition}
      frameTriggerWhenOpen
      locked={displayLocked}
      onOpenChange={handleOpenChange}
      onTriggerClick={handleToggle}
      opened={selected}
      showEarnCounts
      p={6}
    >
      {medallion}
    </BadgePopover>
  )
})
